import { useSelector } from 'react-redux';
import { call, put, select } from 'redux-saga/effects';
import { createSelector } from 'reselect';

import {
  HYDRATE,
  type HydrateAction,
} from '@/domains/core/applicationState/store/hydrateAction';
import { fetchMdScores } from '@/domains/productDiscovery/ArticleReviews/articleReviews.api';
import type {
  ArticleReviewsState,
  MdScore,
  MdScoresState,
} from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import type { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';
import {
  GET_DETAIL_SUCCESS,
  type GetDetailSuccess,
} from '@/domains/productDiscovery/ProductPage/detail.actions';
import { detailSelector } from '@/domains/productDiscovery/ProductPage/selectors/detail';

export const GET_MD_SCORES = 'product/GET_MD_SCORES';

export interface GetMdScores {
  type: typeof GET_MD_SCORES;
  articleId: number;
}

export const GET_MD_SCORES_SUCCESS = 'product/GET_MD_SCORES_SUCCESS';

export interface GetMdScoresSuccess {
  type: typeof GET_MD_SCORES_SUCCESS;
  mdScores: MdScore[];
}

export const GET_MD_SCORES_FAILURE = 'product/GET_MD_SCORES_FAILURE';

export interface GetMdScoresFailure {
  type: typeof GET_MD_SCORES_FAILURE;
  error: string;
}

export type Action =
  | GetMdScores
  | GetMdScoresSuccess
  | GetMdScoresFailure
  | GetDetailSuccess
  | HydrateAction<SpartacuxProductDiscoveryDomainState>;

export const initialMdScoresState: MdScoresState = {
  fetching: false,
  fetched: false,
  articleId: null,
  data: [],
};

// SELECTORS
export const mdScoresSelector = (state: SpartacuxProductDiscoveryDomainState) =>
  (state?.productDiscovery?.articleReviews as ArticleReviewsState | undefined)
    ?.mdScores || initialMdScoresState;
export const useMdScores = () => useSelector(mdScoresSelector);

// REDUCER
export default function reducer(
  state: MdScoresState = initialMdScoresState,
  action: Action,
): MdScoresState {
  switch (action.type) {
    case GET_MD_SCORES:
      return {
        data: [],
        articleId: action.articleId,
        fetched: false,
        fetching: true,
      };

    case GET_MD_SCORES_SUCCESS:
      return {
        ...state,
        data: action.mdScores.map((md) => ({
          ...md,
          hasRanking: md.ranking.length >= 5,
          rankingPosition:
            md.ranking.find((item) => item.articleId === state.articleId)
              ?.position || null,
        })),
        fetching: false,
        fetched: true,
      };

    case GET_MD_SCORES_FAILURE:
      return {
        ...state,
        data: [],
        error: action.error,
        fetching: false,
        fetched: true,
      };

    case GET_DETAIL_SUCCESS:
      if (action.detail.mdScores) {
        return {
          data: action.detail.mdScores.map((md) => ({
            ...md,
            hasRanking: md.ranking.length >= 5,
            rankingPosition:
              md.ranking.find(
                (item) => item.articleId === action.detail.articleId,
              )?.position || null,
          })),
          articleId: action.detail.articleId,
          fetching: false,
          fetched: true,
        };
      }
      return initialMdScoresState;

    case HYDRATE:
      return mdScoresSelector(action.state);

    default:
      return state;
  }
}

/*
  this may look wierd but it was implemented following redux docs example
  https://react-redux.js.org/api/hooks#using-memoizing-selectors
 */
export const selectMdrById = createSelector(
  mdScoresSelector,
  (_: any, id: number) => id,
  (mdScores, id) => mdScores.data.find((md: MdScore) => md.questionId === id),
);

// SAGA
interface actionWithArticleId {
  type: string;
  articleId: number;
}

export function* getMdScoresSaga(
  action: actionWithArticleId,
): Generator<any, any, any> {
  try {
    const mdScoresState = yield select(mdScoresSelector);
    const { categoryId } = yield select(detailSelector);

    if (mdScoresState.fetched && mdScoresState.articleId === action.articleId)
      return null;

    yield put({
      type: GET_MD_SCORES,
      articleId: action.articleId,
    });
    const mdScores = yield call(fetchMdScores, action.articleId, categoryId);

    yield put({
      type: GET_MD_SCORES_SUCCESS,
      mdScores,
    });
  } catch (error) {
    yield put({
      type: GET_MD_SCORES_FAILURE,
      error: error.message,
    });
  }
}
